/**
 * Nimbus PDF engine — a small programmatic document builder shared by every register export.
 *
 * Deliberately NOT html2canvas. The `pdfExport.ts` helper rasterises a DOM node onto one
 * custom-height page, which means the output is an image (no selectable text, no search) and
 * cannot paginate — the only way to split a bitmap across A4 pages is to slice it, which cuts
 * through whatever happens to sit on the boundary.
 *
 * This draws the document programmatically instead, which buys the thing that was actually
 * asked for: **clean page breaks**. Every block is measured before it is placed, and if it
 * does not fit in the remaining space it moves to the next page whole. Specifically:
 *
 *   - A section heading never lands at the bottom of a page away from its content: a
 *     heading reserves space for itself plus its first row before it commits.
 *   - A table row is never split across pages. Row height is computed from its wrapped
 *     text first, then checked against the space left.
 *   - When a table does continue onto a new page, its column headers repeat.
 *   - Key/value rows, paragraphs and chips are all measured the same way.
 *
 * Styling echoes the app rather than screenshotting it: the same purple primary, the same
 * tone colours for chips, the same type hierarchy.
 *
 * The class knows nothing about any particular register. Callers supply a footer label and
 * compose the primitives below; see `aiUseCasePdf.ts` and `meetingMinutesPdf.ts`.
 */
import { jsPDF } from 'jspdf';

// ─── Page geometry (A4 portrait, mm) ──────────────────────────────────────────

export const PAGE_W = 210;
export const PAGE_H = 297;
export const MARGIN_X = 16;
export const MARGIN_TOP = 18;
export const MARGIN_BOTTOM = 18;
export const CONTENT_W = PAGE_W - MARGIN_X * 2;

// ─── Palette — mirrors the app's real tokens ──────────────────────────────────

export type RGB = [number, number, number];

export const INK: RGB = [42, 38, 53];          // --bs-heading-color
export const BODY: RGB = [90, 85, 105];        // --bs-body-color, slightly lifted for print
export const MUTED: RGB = [140, 136, 152];
export const RULE: RGB = [226, 224, 233];      // --np-border
export const PRIMARY: RGB = [140, 87, 255];    // --bs-primary #8c57ff
export const SURFACE_2: RGB = [248, 247, 250];

export const TONES: Record<string, { fg: RGB; bg: RGB }> = {
  success:  { fg: [58, 148, 0],   bg: [233, 246, 224] },
  warning:  { fg: [176, 122, 0],  bg: [255, 244, 219] },
  danger:   { fg: [199, 43, 47],  bg: [255, 232, 232] },
  info:     { fg: [10, 130, 175], bg: [225, 245, 252] },
  primary:  { fg: [107, 61, 214], bg: [239, 231, 255] },
  neutral:  { fg: [110, 106, 122], bg: [240, 239, 244] },
  dark:     { fg: [58, 54, 70],   bg: [232, 231, 238] },
};

const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
const mixColor = (a: RGB, b: RGB, t: number): RGB => [lerp(a[0], b[0], t), lerp(a[1], b[1], t), lerp(a[2], b[2], t)];

// Purpose-built gradient for the heat map grid - green/amber/red the way a risk heat map
// conventionally reads, independent of the chip TONES above (those are tuned for pale chip
// backgrounds under dark text, too washed out to read as "low/med/high" fills at a glance).
const HEAT_LOW: RGB = [200, 230, 170];
const HEAT_MID: RGB = [255, 214, 110];
const HEAT_HIGH: RGB = [230, 80, 80];
const heatColor = (t: number): RGB => (t <= 0.5 ? mixColor(HEAT_LOW, HEAT_MID, t / 0.5) : mixColor(HEAT_MID, HEAT_HIGH, (t - 0.5) / 0.5));

// ─── Type scale ───────────────────────────────────────────────────────────────

export const T = {
  docTitle:   { size: 20, style: 'bold'   as const, colour: INK,     lead: 8.0 },
  ucTitle:    { size: 14, style: 'bold'   as const, colour: INK,     lead: 6.4 },
  section:    { size: 10, style: 'bold'   as const, colour: PRIMARY, lead: 5.4 },
  label:      { size: 7.6, style: 'bold'  as const, colour: MUTED,   lead: 3.8 },
  body:       { size: 9,  style: 'normal' as const, colour: BODY,    lead: 4.4 },
  bodyStrong: { size: 9,  style: 'bold'   as const, colour: INK,     lead: 4.4 },
  small:      { size: 7.6, style: 'normal' as const, colour: MUTED,  lead: 3.6 },
  th:         { size: 7.4, style: 'bold'  as const, colour: MUTED,   lead: 3.6 },
  td:         { size: 8.2, style: 'normal' as const, colour: BODY,   lead: 3.9 },
};
export type Style = typeof T[keyof typeof T];

/** A table cell is either plain text or a tone-coloured chip. */
export type Cell = string | { text: string; tone: string };

/** A line that could not be made to fit the box it was wrapped into. */
export interface Overflow { text: string; width: number; box: number }

// ─── HTML → plain text, preserving paragraph structure ────────────────────────

/** Rich-text fields are ReactQuill HTML. Flatten to paragraphs so the PDF keeps the
 *  author's breaks instead of running everything together. */
export const htmlToParagraphs = (html: string): string[] => {
  if (!html) return [];
  const withBreaks = html
    .replace(/<\s*br\s*\/?\s*>/gi, '\n')
    .replace(/<\/\s*(p|div|li|h[1-6])\s*>/gi, '\n')
    .replace(/<\s*li[^>]*>/gi, '• ');
  const doc = new DOMParser().parseFromString(withBreaks, 'text/html');
  return (doc.body.textContent || '')
    .split('\n')
    .map(s => s.replace(/ /g, ' ').trim())
    .filter(Boolean);
};

/** Rich text flattened to plain text for table cells — paragraph/bullet breaks are kept as
 *  real newlines (not collapsed to spaces), since the table cell renderer (`wrap`, via
 *  jsPDF's `splitTextToSize`) already treats `\n` as a hard line break. Joining with a space
 *  instead would run every paragraph and bullet together into one undifferentiated block. */
export const stripHtml = (html: string): string => htmlToParagraphs(html).join('\n');

// ─── The document builder ─────────────────────────────────────────────────────

export class Doc {
  private pdf: jsPDF;
  private y = MARGIN_TOP;
  private pageNo = 1;
  private generatedAt: string;
  /** Printed bottom-left on every page, e.g. 'Mercell Nimbus — AI Use Case Register'. */
  private footerLabel: string;
  /** Deepest cursor position reached on each page. Exposed so the pagination
   *  guarantee ("nothing crosses the bottom margin") is assertable in a test
   *  rather than eyeballed in a viewer. */
  readonly maxYByPage: number[] = [];
  /** Lines that came back from wrapping still wider than the box they were meant to
   *  fit — i.e. horizontal overflow. The original verification only checked vertical
   *  pagination, which is how the Ownership section's unwrapped 58mm keys shipped into
   *  a 46mm gutter. Exposed so a test can assert columns never collide. */
  readonly overflows: Overflow[] = [];

  constructor(generatedAt: string, footerLabel: string) {
    this.pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' });
    this.pdf.setFont('helvetica', 'normal');
    this.generatedAt = generatedAt;
    this.footerLabel = footerLabel;
  }

  // — low-level —

  private apply(s: Style) {
    this.pdf.setFont('helvetica', s.style);
    this.pdf.setFontSize(s.size);
    this.pdf.setTextColor(...s.colour);
  }

  private wrap(text: string, style: Style, width: number): string[] {
    this.apply(style);
    const lines = this.pdf.splitTextToSize(text, width) as string[];
    for (const line of lines) {
      const w = this.pdf.getTextWidth(line);
      // splitTextToSize cannot break a single token longer than the box, so a line can
      // still come back too wide. That is a genuine layout fault, not a rounding artefact.
      if (w > width + 0.5) this.overflows.push({ text: line, width: w, box: width });
    }
    return lines;
  }

  /** Space left before the footer. */
  private get remaining(): number {
    return PAGE_H - MARGIN_BOTTOM - this.y;
  }

  /** Record the bottom edge of content actually drawn. Called by the draw primitives,
   *  never by trailing whitespace — a cursor parked past the floor with nothing under
   *  it is harmless, real ink below the floor is not. */
  private mark(): void {
    const i = this.pageNo - 1;
    if (this.maxYByPage[i] === undefined || this.y > this.maxYByPage[i]) {
      this.maxYByPage[i] = this.y;
    }
  }

  /** The core of clean pagination: reserve `h` mm, moving to a new page if it won't fit. */
  private reserve(h: number): void {
    if (h <= this.remaining) return;
    this.newPage();
  }

  private newPage(): void {
    this.footer();
    this.pdf.addPage();
    this.pageNo += 1;
    this.y = MARGIN_TOP;
  }

  private footer(): void {
    const yy = PAGE_H - 10;
    this.pdf.setDrawColor(...RULE);
    this.pdf.setLineWidth(0.2);
    this.pdf.line(MARGIN_X, yy - 4, PAGE_W - MARGIN_X, yy - 4);
    this.apply(T.small);
    this.pdf.text(this.footerLabel, MARGIN_X, yy);
    this.pdf.text(`Page ${this.pageNo}`, PAGE_W - MARGIN_X, yy, { align: 'right' });
    this.pdf.text(this.generatedAt, PAGE_W / 2, yy, { align: 'center' });
  }

  // — text primitives —

  paragraph(text: string, style: Style = T.body, opts: { indent?: number; gap?: number } = {}): void {
    const indent = opts.indent ?? 0;
    const lines = this.wrap(text, style, CONTENT_W - indent);
    for (const line of lines) {
      this.reserve(style.lead);
      this.apply(style);
      this.pdf.text(line, MARGIN_X + indent, this.y + style.lead * 0.75);
      this.y += style.lead;
    }
    this.mark();
    this.y += opts.gap ?? 0;
  }

  /** Heading that refuses to be orphaned: it reserves its own height plus `withNext` mm
   *  of whatever follows, so a heading never sits alone at the foot of a page. */
  sectionHeading(text: string, withNext = 12): void {
    const h = T.section.lead + 3;
    this.reserve(h + withNext);
    this.y += 2;
    this.apply(T.section);
    this.pdf.text(text.toUpperCase(), MARGIN_X, this.y + T.section.lead * 0.75);
    this.y += T.section.lead;
    this.pdf.setDrawColor(...RULE);
    this.pdf.setLineWidth(0.3);
    this.pdf.line(MARGIN_X, this.y, PAGE_W - MARGIN_X, this.y);
    this.mark();
    this.y += 2.6;
  }

  /** Label above value, the app's field pattern. Measured as one unit so the label never
   *  separates from its value. */
  field(label: string, value: string, opts: { empty?: string } = {}): void {
    const text = value?.trim() ? value : (opts.empty ?? 'Not provided');
    const isEmpty = !value?.trim();
    const style = isEmpty ? T.small : T.body;
    const lines = this.wrap(text, style, CONTENT_W);
    this.reserve(T.label.lead + lines.length * style.lead + 2);
    this.apply(T.label);
    this.pdf.text(label.toUpperCase(), MARGIN_X, this.y + T.label.lead * 0.75);
    this.y += T.label.lead;
    for (const line of lines) {
      this.apply(style);
      this.pdf.text(line, MARGIN_X, this.y + style.lead * 0.75);
      this.y += style.lead;
    }
    this.mark();
    this.y += 2;
  }

  /** Label plus multiple paragraphs (rich-text fields). The label is measured together
   *  with the FIRST paragraph line only — a long field is allowed to flow across a page
   *  break, but its heading can never be stranded at the bottom of one. */
  fieldParagraphs(label: string, paragraphs: string[]): void {
    const firstLines = this.wrap(paragraphs[0] ?? '', T.body, CONTENT_W);
    this.reserve(T.label.lead + Math.min(firstLines.length, 2) * T.body.lead + 2);
    this.apply(T.label);
    this.pdf.text(label.toUpperCase(), MARGIN_X, this.y + T.label.lead * 0.75);
    this.y += T.label.lead;
    paragraphs.forEach((para, i) => {
      const lines = this.wrap(para, T.body, CONTENT_W);
      lines.forEach(line => {
        this.reserve(T.body.lead);
        this.apply(T.body);
        this.pdf.text(line, MARGIN_X, this.y + T.body.lead * 0.75);
        this.y += T.body.lead;
      });
      if (i < paragraphs.length - 1) this.y += 1.4;
    });
    this.mark();
    this.y += 2.4;
  }

  /** Free-flowing paragraphs with no label, used under a section heading. Each paragraph
   *  is measured line by line, so a long body flows across pages without ever being cut. */
  paragraphs(paras: string[], style: Style = T.body): void {
    paras.forEach((para, i) => {
      this.paragraph(para, style, { gap: i < paras.length - 1 ? 1.4 : 0 });
    });
    this.y += 2.4;
  }

  /** Two-column key/value. Both columns wrap and the row takes the taller of the two,
   *  so a long key can never run underneath its value — that was a real defect: the five
   *  RACI keys are 46-58mm against what used to be a fixed, unwrapped 46mm gutter. */
  metaRow(key: string, value: string): void {
    const keyW = 52;
    const gap = 3;
    const valW = CONTENT_W - keyW - gap;
    const keyLines = this.wrap(key.toUpperCase(), T.label, keyW);
    const valLines = this.wrap(value || '—', T.body, valW);
    const h = Math.max(keyLines.length * T.label.lead, valLines.length * T.body.lead);

    this.reserve(h + 1);

    let ky = this.y;
    this.apply(T.label);
    keyLines.forEach(line => {
      this.pdf.text(line, MARGIN_X, ky + T.label.lead * 0.75);
      ky += T.label.lead;
    });

    let vy = this.y;
    this.apply(T.body);
    valLines.forEach(line => {
      this.pdf.text(line, MARGIN_X + keyW + gap, vy + T.body.lead * 0.75);
      vy += T.body.lead;
    });

    this.y += h;
    this.mark();
    this.y += 1;
  }

  /**
   * A bordered card, matching the app's own .np-card visual language: a bold title, then
   * whatever `draw` renders, framed by a light rounded-rect outline. The frame is measured
   * after the fact rather than pre-computed — `draw` runs first (using every normal primitive,
   * including ones that paginate internally like `table`), its start/end y are recorded, then a
   * stroke-only rect is painted over the result. An outline drawn on top of already-placed text
   * doesn't obscure it, which is what makes this two-pass approach possible without threading a
   * narrower content width through every other primitive in this file.
   *
   * Reserves a small amount of headroom before starting so a card's title is never orphaned
   * alone at the bottom of a page — a card taller than one page's remaining space still has its
   * frame span the break cleanly, same as any other multi-page content this engine draws.
   */
  card(title: string, draw: () => void): void {
    this.reserve(16);
    this.y += 3;
    const startY = this.y;
    this.apply(T.section);
    this.pdf.setTextColor(...PRIMARY);
    this.pdf.text(title.toUpperCase(), MARGIN_X, this.y + T.section.lead * 0.75);
    this.y += T.section.lead + 2;
    draw();
    this.mark();
    this.y += 3;
    const endY = this.y;
    this.pdf.setDrawColor(...RULE);
    this.pdf.setLineWidth(0.4);
    this.pdf.roundedRect(MARGIN_X - 3, startY - 3, CONTENT_W + 6, endY - startY + 3, 2.5, 2.5, 'S');
    this.y += 5;
  }

  /** A row of small bordered stat tiles side by side, matching the app's own stat-tile grid
   *  (which sits above its chart cards, not inside one) - a label, then a large value, each in
   *  its own box. Reserves the whole row as one unit; a row is short enough that letting it
   *  split mid-row across a page break would look worse than just moving the whole thing down. */
  statRow(stats: Array<{ label: string; value: string }>): void {
    if (stats.length === 0) return;
    const gap = 4;
    const boxH = 20;
    const boxW = (CONTENT_W - gap * (stats.length - 1)) / stats.length;
    this.reserve(boxH + 5);
    const top = this.y;
    stats.forEach((s, i) => {
      const x = MARGIN_X + i * (boxW + gap);
      this.pdf.setDrawColor(...RULE);
      this.pdf.setLineWidth(0.3);
      this.pdf.roundedRect(x, top, boxW, boxH, 2, 2, 'S');
      this.apply(T.label);
      this.pdf.text(s.label.toUpperCase(), x + 3.5, top + 7);
      this.apply({ ...T.docTitle, size: 15, lead: T.docTitle.lead });
      this.pdf.text(s.value, x + 3.5, top + 16);
    });
    this.y = top + boxH;
    this.mark();
    this.y += 5;
  }

  /**
   * A horizontal bar chart: label, a track, a filled bar scaled to the largest value in the
   * set, and the value itself. Each bar is its own row so it paginates the same way `table`'s
   * rows do - one bar is never split across a page break, and the chart simply continues on the
   * next page if it runs out of room, with no header to repeat.
   */
  barChart(items: Array<{ label: string; value: number; tone?: string }>): void {
    if (items.length === 0) return;
    const rowH = 8;
    const labelW = 46;
    const valueW = 14;
    const trackW = CONTENT_W - labelW - valueW - 4;
    const maxValue = Math.max(1, ...items.map((i) => i.value));

    items.forEach((item) => {
      if (rowH + 1 > this.remaining) this.newPage();
      const labelLines = this.wrap(item.label, T.td, labelW - 2);
      this.apply(T.td);
      this.pdf.text(labelLines[0] ?? '', MARGIN_X, this.y + rowH * 0.6);

      const trackX = MARGIN_X + labelW;
      this.pdf.setFillColor(...SURFACE_2);
      this.pdf.rect(trackX, this.y + 1.5, trackW, rowH - 4, 'F');

      const fillW = (item.value / maxValue) * trackW;
      if (fillW > 0) {
        const tone = TONES[item.tone ?? 'primary'] ?? TONES.primary;
        this.pdf.setFillColor(...tone.fg);
        this.pdf.rect(trackX, this.y + 1.5, Math.max(fillW, 1.2), rowH - 4, 'F');
      }

      this.apply(T.small);
      this.pdf.setTextColor(...BODY);
      this.pdf.text(String(item.value), trackX + trackW + 3, this.y + rowH * 0.6);
      this.y += rowH;
    });
    this.mark();
    this.y += 2;
  }

  /**
   * A colored grid, green -> amber -> red by cell value relative to the highest cell in the
   * whole grid - the PDF equivalent of the app's own ApexCharts heat map. Reserved and drawn as
   * one unit rather than row-by-row like `table`/`barChart`: a heat map split across a page
   * break would lose the point of being a single at-a-glance grid.
   */
  heatmapGrid(rowLabels: string[], colLabels: string[], matrix: number[][]): void {
    if (matrix.length === 0) return;
    const cellH = 9;
    const labelColW = 30;
    const cellW = (CONTENT_W - labelColW) / colLabels.length;
    const totalH = (matrix.length + 1) * cellH;
    this.reserve(totalH + 2);

    const maxVal = Math.max(1, ...matrix.flat());

    this.apply(T.th);
    let x = MARGIN_X + labelColW;
    colLabels.forEach((label) => {
      this.pdf.text(label, x + cellW / 2, this.y + cellH * 0.65, { align: 'center' });
      x += cellW;
    });
    this.y += cellH;

    matrix.forEach((row, ri) => {
      this.apply(T.th);
      this.pdf.setTextColor(...MUTED);
      this.pdf.text(rowLabels[ri] ?? '', MARGIN_X, this.y + cellH * 0.65);

      let cx = MARGIN_X + labelColW;
      row.forEach((value) => {
        const t = value / maxVal;
        this.pdf.setFillColor(...heatColor(t));
        this.pdf.rect(cx, this.y, cellW - 1, cellH - 1, 'F');
        this.apply({ ...T.small, style: 'bold', colour: t > 0.55 ? [255, 255, 255] : INK });
        this.pdf.text(String(value), cx + cellW / 2, this.y + cellH * 0.62, { align: 'center' });
        cx += cellW;
      });
      this.y += cellH;
    });
    this.mark();
    this.y += 3;
  }

  chip(text: string, tone: string, x: number, yTop: number): number {
    const t = TONES[tone] ?? TONES.neutral;
    this.apply({ ...T.small, style: 'bold', colour: t.fg });
    const w = this.pdf.getTextWidth(text) + 4;
    this.pdf.setFillColor(...t.bg);
    this.pdf.roundedRect(x, yTop, w, 4.6, 1.2, 1.2, 'F');
    this.pdf.text(text, x + 2, yTop + 3.3);
    return w;
  }

  /**
   * Table with row-level pagination. Rows are measured from their wrapped content and
   * placed whole — a row never straddles a page boundary. Headers repeat on continuation.
   */
  table(headers: string[], widths: number[], rows: Cell[][]): void {
    const drawHeader = () => {
      const h = 6;
      this.reserve(h + 6);   // header + at least one row's worth
      this.pdf.setFillColor(...SURFACE_2);
      this.pdf.rect(MARGIN_X, this.y, CONTENT_W, h, 'F');
      this.apply(T.th);
      let x = MARGIN_X + 2;
      headers.forEach((label, i) => {
        this.pdf.text(label.toUpperCase(), x, this.y + h * 0.68);
        x += widths[i];
      });
      this.y += h;
    };

    drawHeader();

    rows.forEach((row) => {
      // measure first
      const cellLines = row.map((cell, i) => {
        const text = typeof cell === 'string' ? cell : cell.text;
        return typeof cell === 'string'
          ? this.wrap(text || '—', T.td, widths[i] - 4)
          : [text];
      });
      const rowH = Math.max(...cellLines.map(l => l.length)) * T.td.lead + 2.6;

      // then decide
      if (rowH > this.remaining) {
        this.newPage();
        drawHeader();
      }

      let x = MARGIN_X + 2;
      row.forEach((cell, i) => {
        if (typeof cell === 'string') {
          this.apply(T.td);
          cellLines[i].forEach((line, li) => {
            this.pdf.text(line, x, this.y + 2 + T.td.lead * (li + 0.75));
          });
        } else {
          this.chip(cell.text, cell.tone, x, this.y + 1.4);
        }
        x += widths[i];
      });

      this.y += rowH;
      this.pdf.setDrawColor(...RULE);
      this.pdf.setLineWidth(0.15);
      this.pdf.line(MARGIN_X, this.y, PAGE_W - MARGIN_X, this.y);
      this.mark();
    });

    this.y += 3;
  }

  space(mm: number): void { this.y += mm; }

  /** Force the next content onto a fresh page — used between records so each starts clean. */
  pageBreak(): void {
    this.footer();
    this.pdf.addPage();
    this.pageNo += 1;
    this.y = MARGIN_TOP;
  }

  coverTitle(title: string, subtitle: string): void {
    this.apply(T.docTitle);
    this.pdf.text(title, MARGIN_X, this.y + T.docTitle.lead * 0.75);
    this.y += T.docTitle.lead + 1;
    this.apply(T.body);
    this.pdf.setTextColor(...MUTED);
    this.pdf.text(subtitle, MARGIN_X, this.y + T.body.lead * 0.75);
    this.y += T.body.lead + 4;
    this.pdf.setDrawColor(...PRIMARY);
    this.pdf.setLineWidth(0.8);
    this.pdf.line(MARGIN_X, this.y, MARGIN_X + 26, this.y);
    this.y += 6;
  }

  /**
   * The header for one record inside the document: a small id eyebrow, the record title,
   * and a row of chips. Eyebrow, title and chips are measured together so they never split
   * across a page break.
   */
  recordHeading(eyebrow: string, title: string, chips: Array<{ text: string; tone: string }> = []): void {
    const titleLines = this.wrap(title, T.ucTitle, CONTENT_W);
    this.reserve(titleLines.length * T.ucTitle.lead + 12);

    this.apply(T.label);
    this.pdf.text(eyebrow, MARGIN_X, this.y + T.label.lead * 0.75);
    this.y += T.label.lead + 0.5;

    this.apply(T.ucTitle);
    titleLines.forEach(line => {
      this.pdf.text(line, MARGIN_X, this.y + T.ucTitle.lead * 0.75);
      this.y += T.ucTitle.lead;
    });
    this.y += 1.5;

    if (chips.length) {
      let x = MARGIN_X;
      chips.forEach(c => { x += this.chip(c.text, c.tone, x, this.y) + 2; });
      this.y += 4.6;
    }
    this.mark();
    this.y += 3.4;
  }

  finish(): { pdf: jsPDF; pages: number; maxYByPage: number[]; overflows: Overflow[] } {
    this.footer();
    return {
      pdf: this.pdf,
      pages: this.pdf.getNumberOfPages(),
      maxYByPage: [...this.maxYByPage],
      overflows: [...this.overflows],
    };
  }
}

// ─── Id resolution ─────────────────────────────────────────────────────────────
//
// Moved here from aiUseCasePdf.ts, which is where it was first written — there was nothing
// AI-specific about it even then. A second register (Processing Activities) needing the
// exact same "resolve or say so honestly" behaviour is what surfaced that it belonged at
// this level rather than being copied a second time.

/** Turns a stored id into a display name. Supplied by the caller because this module has no
 *  data access, and different id fields resolve against different registers (a personId
 *  isn't a userId isn't a departmentId). The report page builds one map from whichever of
 *  those the record actually uses and passes it in. */
export type NameLookup = (id: string) => string | undefined;

/**
 * An id-valued field, printed honestly. Either it resolves to a name, or the label says the
 * value is a stored id — what it must never do is print `USER-4F2A1C8B` under a heading that
 * says "Approved by", which is what happens on the Meeting Minutes detail page when an
 * approver's userId is looked up in a personId-keyed map.
 */
export function identityField(doc: Doc, label: string, id: string, lookup?: NameLookup): void {
  if (!id) {
    doc.field(label, '', { empty: 'Not recorded' });
    return;
  }
  const name = lookup?.(id);
  if (name) doc.field(label, name);
  else doc.field(`${label} (stored id)`, id);
}

// ─── Shared build result ──────────────────────────────────────────────────────

export interface PdfBuildResult {
  pages: number;
  /** Deepest cursor position reached per page, in mm. Must never exceed
   *  PAGE_H - MARGIN_BOTTOM (279) — that is the "content is never cut by a page
   *  break" guarantee, expressed as something a test can assert. */
  maxYByPage: number[];
  /** Any line that could not be made to fit its column. Must be empty. */
  overflows: Overflow[];
  filename: string;
  save: () => void;
}
